import React from 'react';
import { useQuery } from 'react-query';
import { useAppSession } from '../contexts/AppSessionContext';
import { auth } from '../sp-firebase/firebase';
import { useFx } from './useFx';
import { useBaseCurrency } from './useBaseCurrency';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

async function waitForFirebaseUser(): Promise<void> {
  if (auth.currentUser) return;
  await new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        unsubscribe();
        resolve(user);
      }
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user');
  }
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

interface FormulaPrice {
  id: number;
  accountId: number;
  formulaId: number;
  formulaCode: string;
  formulaName: string;
  formulaUnit: string;
  settings: any;
  quoteSourceId: number | null;
  quoteSourceSymbol: string | null;
  currentPrice: number | null;
  priceDate: string | null;
  quoteCurrency: string | null;
  quoteUnit: string | null;
}

interface FormulaPricesResponse {
  success: boolean;
  formulas: FormulaPrice[];
}

interface PriceInfo {
  price: number;
  currency: string;
  /** Date of the latest stored price (ISO), when known. */
  priceDate?: string | null;
  originalPrice?: number;
  originalCurrency?: string;
}

interface PricesData {
  pricesMap: Record<string, PriceInfo>;
  asOfDate: string | null;
}

async function getFormulasPrices(accountId: number): Promise<PricesData> {
  await waitForFirebaseUser();
  const headers = await getAuthHeaders();
  
  const url = `${API_URL}/api/v2/accounts/${accountId}/formulas`;
  const response = await fetch(url, { headers });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch formulas prices: ${response.statusText}`);
  }
  
  const data: FormulaPricesResponse = await response.json();
  
  // Store price with its currency
  const pricesMap: Record<string, PriceInfo> = {};
  let asOfDate: string | null = null;
  
  data.formulas.forEach(formula => {
    if (formula.currentPrice !== null) {
      pricesMap[formula.formulaCode] = {
        price: Number(formula.currentPrice), // Ensure it's a number
        currency: formula.quoteCurrency || 'USD',
        priceDate: formula.priceDate ?? null,
      };
      
      // Use the most recent price date we find
      if (formula.priceDate && (!asOfDate || formula.priceDate > asOfDate)) {
        asOfDate = formula.priceDate;
      }
    }
  });
  
  return { pricesMap, asOfDate };
}

export function useFormulasPrices() {
  const { user } = useAppSession();
  const accountId = user?.accountId ? parseInt(user.accountId) : undefined;
  const baseCurrency = useBaseCurrency();
  const { getRateToBase } = useFx(baseCurrency);

  const query = useQuery(
    ['formulas-prices', accountId],
    () => getFormulasPrices(accountId!),
    {
      enabled: !!accountId,
      staleTime: 5 * 60 * 1000, // 5 minutes - prices don't change that often
      refetchOnWindowFocus: false,
    }
  );

  // Convert all prices to USD using real FX rates
  const dataWithUsdPrices = React.useMemo(() => {
    if (!query.data) return query.data;

    const convertedPricesMap: Record<string, PriceInfo> = {};
    
    Object.entries(query.data.pricesMap).forEach(([formulaCode, priceInfo]) => {
      let priceInBase = Number(priceInfo.price); // Ensure numeric
      
      // Convert to base currency if not already in base
      if (priceInfo.currency && priceInfo.currency.toUpperCase() !== baseCurrency.toUpperCase()) {
        const rate = getRateToBase(priceInfo.currency);
        if (rate !== null && rate !== 0) {
          // Rates are stored as: 1 BASE = rate * CURRENCY
          // To convert CURRENCY → BASE we divide: amount / rate
          // Examples:
          // - If BASE=USD: 1 USD = 0.85 EUR, so €9554 / 0.85 = $11,234 USD
          // - If BASE=EUR: 1 EUR = 1.07 USD, so $10,000 / 1.07 ≈ €9,346
          priceInBase = Number(priceInfo.price) / rate;
        }
      }
      
      convertedPricesMap[formulaCode] = {
        price: priceInBase,
        currency: baseCurrency,
        originalPrice: priceInfo.price,
        originalCurrency: priceInfo.currency,
        priceDate: priceInfo.priceDate,
      };
    });

    return {
      pricesMap: convertedPricesMap,
      asOfDate: query.data.asOfDate,
    };
  }, [query.data, getRateToBase, baseCurrency]);

  return {
    ...query,
    data: dataWithUsdPrices,
  };
}
